/**
 * What the learning editor starts with when the server's prefill is not available, read from the chat's diagnosis state
 * (tse:diag_state, as the brain panel holds it):
 *   category   the diagnosis' category (shown, not chosen); its label comes from the panel's category bundle when it has one
 *   symptoms   the symptoms the engineer confirmed, then the facts gathered (stated, then answered), then what was noted from their words;
 *              a denied or unknown one is left out. Each line keeps where it came from (lineMeta, by exact text)
 *   steps      the checks that were run (done or failed) for the leading cause, in the order the diagnosis lists them
 *   rootCause  the name of the leading cause
 * The engineer edits all of it; an empty list shows one blank row. Pure function, safe on a missing or partial state.
 */
import { LINE_SOURCE, packLines } from "./prefillLines";

const SYMPTOM_PREFIX = "SYM_";
const FACT_PREFIX = "F_";
const NOT_PRESENT = new Set(["no", "unknown"]);
const LEADING = new Set(["leading", "confirmed"]);
const RAN = new Set(["done", "failed"]);
const STATED = new Set(["handoff", "reader"]);
const list = (v) => (Array.isArray(v) ? v : []);
const text = (v) => String(v || "").trim();

const present = (f) => f && !NOT_PRESENT.has(f.value);
const idOf = (f) => String(f && f.id);
const fromFact = (f, source) => ({ text: f.label, source, origin: f.source || null, ref_id: f.id || null, hedged: false });

export function sessionFromDiag(diagState, categoryLabel = "") {
  const ds = diagState || {};
  const observed = list(ds.observed).filter(present);
  const symptoms = observed.filter((f) => idOf(f).startsWith(SYMPTOM_PREFIX)).map((f) => fromFact(f, LINE_SOURCE.SYMPTOM));
  const facts = observed.filter((f) => idOf(f).startsWith(FACT_PREFIX));
  const stated = facts.filter((f) => STATED.has(f.source)).map((f) => fromFact(f, LINE_SOURCE.FACT));
  const answered = facts.filter((f) => !STATED.has(f.source)).map((f) => fromFact(f, LINE_SOURCE.FACT));
  const noted = list(ds.noted).filter(Boolean).map((n) => ({ text: n.text, source: LINE_SOURCE.NOTE, origin: null, ref_id: null, hedged: !!n.hedged }));
  const causes = list(ds.causes);
  const leader = causes.find((c) => c && LEADING.has(c.status)) || causes[0];
  const steps = list(leader && leader.checks).filter((c) => c && RAN.has(c.status))
    .map((c) => ({ text: c.text, source: LINE_SOURCE.STEP, origin: "engine", ref_id: c.step_id || null, hedged: false }));
  const key = text(ds.category);
  const sym = packLines([...symptoms, ...stated, ...answered, ...noted]);
  const stp = packLines(steps);
  return {
    category: key ? { key, label: text(categoryLabel) || key } : null,
    symptoms: sym.texts,
    steps: stp.texts,
    rootCause: text(leader && (leader.name || leader.label)),
    lineMeta: sym.meta,
    stepMeta: stp.meta,
    truncated: { symptoms: sym.truncated, steps: stp.truncated },
  };
}

/** The server's prefill ({category, symptoms: Line[], steps: Line[], root_cause, truncated}) in the editor's session shape. */
export function sessionFromPrefill(data) {
  if (!data || typeof data !== "object") return null;
  const sym = packLines(data.symptoms);
  const stp = packLines(data.steps);
  const cat = data.category && data.category.key ? { key: String(data.category.key), label: text(data.category.label) || String(data.category.key) } : null;
  const server = data.truncated || {};
  return {
    category: cat,
    symptoms: sym.texts,
    steps: stp.texts,
    rootCause: text(data.root_cause),
    lineMeta: sym.meta,
    stepMeta: stp.meta,
    truncated: { symptoms: sym.truncated + (Number(server.symptoms) || 0), steps: stp.truncated + (Number(server.steps) || 0) },
  };
}
